"use client";

import { sanitizeEmail, isValidEmail as validateEmail } from "@health/validation";
import { useCallback, useState } from "react";
import { useResolvedHostUrl } from "../../../host";

export function useAuthForgotPassword(defaultEmail?: string) {
  const forgotPasswordUrl = useResolvedHostUrl("/v1/auth/forgot-password");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [sent, setSent] = useState(false);

  const submit = useCallback(async (): Promise<{ success: boolean }> => {
    setError("");

    const sanitizedEmail = sanitizeEmail(email);
    if (!validateEmail(sanitizedEmail)) {
      setError("Please enter a valid email address");
      return { success: false };
    }

    setIsLoading(true);
    try {
      const response = await fetch(forgotPasswordUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: sanitizedEmail }),
        credentials: "include",
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok && response.status === 429) {
        setError(data.message || "Too many requests. Please try again in a moment.");
        setIsLoading(false);
        return { success: false };
      }

      setSent(true);
      setIsLoading(false);
      return { success: true };
    } catch {
      setError("Unable to connect. Please check your internet connection and try again.");
      setIsLoading(false);
      return { success: false };
    }
  }, [email, forgotPasswordUrl]);

  const resend = useCallback(async () => {
    setIsLoading(true);
    await submit();
  }, [submit]);

  const reset = useCallback(() => {
    setSent(false);
    setError("");
    setIsLoading(false);
  }, []);

  return {
    email,
    setEmail,
    isLoading,
    error,
    setError,
    sent,
    submit,
    resend,
    reset,
  };
}
